/**
 * The pieces the employee's own pages share (owner's employee UI brief, 2026-09-22): the work
 * status card, a shift row, a person row with its permitted contact, the shift detail drawer
 * (Vaktdetaljer, with its tasks and its offer and swap actions) and the open-shift ask. Every
 * call is a route the phone already uses (docs/behaviour/mobile.md); nothing here is a manager's.
 */
import { ApiError, errorText } from '@/api/client';
import {
  type ApiShift,
  type ApiSwap,
  type ApiSwapCandidate,
  type PunchStatus,
  useClaim,
  useMySwaps,
  useMyWrites,
  usePunch,
  useShifts,
  useSwapCandidates,
  useSwapWrites,
} from '@/api/data';
import { projectLabel, useProjectWord, useProjects, useProjectsOn } from '@/api/projects';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { breakPolicy, isPunched, oslo, today, workedSum } from '@/rules';
import { breakForEmployee, modeOf } from '@/rules/breakSetting';
import { type Tenant, useTenant } from '@/shell/tenant';
import { Button, Chip, Drawer, Empty, toast } from '@/ui';
import { Tasks } from '@/ui/Tasks';
import { DN, DNL, avTone, fmtD, fullName, initials, num } from '@/ui/format';
import { Icon, type IconName } from '@/ui/icons';
import { addDays, dow } from '@timefly/core';
import { type ReactNode, useEffect, useState } from 'react';

/* ── small helpers ─────────────────────────────────────────────────────── */

/** Minutes as people say them: "2 t 14 min", "45 min". */
export const dur = (min: number): string => {
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60);
  return h ? `${h} t ${m % 60} min` : `${m} min`;
};
export const minutesBetween = (a: string | null | undefined, b: string | Date | null | undefined) =>
  a && b ? (new Date(b).getTime() - new Date(a).getTime()) / 60000 : 0;
/** The wall clock, re-read every half minute, so an elapsed time moves while the page is open. */
export function useNow(ms = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}
export const span = (s: { start_time: string; end_time: string }) =>
  `${oslo(s.start_time)?.time}–${oslo(s.end_time)?.time}`;
export const lengthH = (s: { start_time: string; end_time: string }) =>
  minutesBetween(s.start_time, s.end_time) / 60;
export const dayOf = (s: { start_time: string }) => oslo(s.start_time)?.date ?? '';
/** "torsdag 24.9.2026" */
export const longDay = (d: string) => (d ? `${DNL[dow(d)]} ${fmtD(d)}` : '');
export const shiftName = (s: ApiShift) => s.shiftType?.name || T('Vakt');
export const taskCount = (s: ApiShift | null | undefined) => {
  const t = s?.taskAssignments ?? [];
  return { done: t.filter((x) => x.status === 'completed').length, total: t.length };
};
/** Breaks are started on screen only where the company records them (tenant.lunch_included false). */
export const recordsBreaks = (tenant: Tenant | undefined) => tenant != null && !tenant.lunch_included;

/* ── visual pieces ─────────────────────────────────────────────────────── */

/** A bar beside a count that is already written out, so the bar itself is decorative. */
export function Progress({
  done,
  total,
  ok,
  used,
}: {
  done: number;
  total: number;
  ok?: boolean;
  used?: boolean;
}) {
  const pct = total ? Math.min(100, Math.max(0, Math.round((done / total) * 100))) : 0;
  return (
    <div className={`ebar${ok ? ' ok' : ''}${used ? ' used' : ''}`} aria-hidden="true">
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

/** A date as a small box: weekday over day and month. */
export const DateBox = ({ d }: { d: string }) => (
  <span className="edate" aria-hidden="true">
    <small>{DN[dow(d)]}</small>
    <b>
      {Number(d.slice(8, 10))}.{Number(d.slice(5, 7))}
    </b>
  </span>
);

export const Avatar = ({
  u,
  lg,
}: { u: { id?: number; name: string; lastname?: string | null } | null | undefined; lg?: boolean }) => (
  <span className={`av ${avTone(u?.id ?? u?.name)}${lg ? ' lg' : ''}`} aria-hidden="true">
    {initials(fullName(u) || '?')}
  </span>
);

/** The shift's state in words and colour together. */
export function ShiftChip({ s }: { s: ApiShift }) {
  if (s.is_approved)
    return (
      <Chip tone="ok" small>
        {T('Godkjent')}
      </Chip>
    );
  if (s.punch_in_time && !s.punch_out_time)
    return (
      <Chip tone="ok" small>
        {T('På jobb')}
      </Chip>
    );
  if (isPunched(s))
    return (
      <Chip tone="neu" small>
        {T('Til godkjenning')}
      </Chip>
    );
  if (!s.published)
    return (
      <Chip tone="neu" small>
        {T('Utkast')}
      </Chip>
    );
  if (s.is_offer_requested)
    return (
      <Chip tone="warn" small>
        {T('Tilbudt bort')}
      </Chip>
    );
  if (s.is_swap_requested)
    return (
      <Chip tone="warn" small>
        {T('Bytte ønsket')}
      </Chip>
    );
  // planned is the plain state: neutral ink, blue is kept for actions (#272 B-35)
  return (
    <Chip tone="neu" small>
      {T('Planlagt')}
    </Chip>
  );
}

/** One shift as a row: date box, time and length, type, place, state. The whole row opens it. */
export function ShiftRow({
  s,
  place,
  onOpen,
  showDate = true,
}: { s: ApiShift; place?: string | undefined; onOpen: () => void; showDate?: boolean }) {
  const t = taskCount(s);
  return (
    <button type="button" className="erow" onClick={onOpen} data-testid="shift-row">
      {showDate && <DateBox d={dayOf(s)} />}
      <span className="ebody">
        <b>
          <span className="mono">{span(s)}</span> <small className="nw">({num(lengthH(s), 1)} t)</small>
        </b>
        <span>
          {shiftName(s)}
          {place ? `, ${place}` : ''}
          {t.total ? `, ${T('{done} av {total} oppgaver', t)}` : ''}
        </span>
      </span>
      <ShiftChip s={s} />
    </button>
  );
}

/** Ring and E-post for a number and an address the person may see; nothing when there is neither. */
export function Contact({
  phone,
  email,
  big,
}: { phone?: string | null | undefined; email?: string | null | undefined; big?: boolean }) {
  if (!phone && !email) return null;
  const a = (href: string, icon: IconName, label: string) => (
    <a
      className={big ? 'btn' : 'eicb'}
      href={href}
      aria-label={big ? undefined : label}
      title={big ? undefined : label}
    >
      <Icon name={icon} />
      {big ? label : null}
    </a>
  );
  return (
    <span className="econtact">
      {phone ? a(`tel:${phone.replace(/\s/g, '')}`, 'phone', T('Ring')) : null}
      {email ? a(`mailto:${email}`, 'mail', T('Send e-post')) : null}
    </span>
  );
}

/** A compact section card with its title inside, for the dashboard pieces. */
export function Card({
  title,
  sub,
  actions,
  children,
  testid,
}: { title: ReactNode; sub?: ReactNode; actions?: ReactNode; children: ReactNode; testid?: string }) {
  return (
    <section className="card2 ecard" data-testid={testid}>
      <div className="ch">
        <b>{title}</b>
        {sub != null && <span className="sub">{sub}</span>}
        <span className="sp" />
        {actions}
      </div>
      <div className="cb">{children}</div>
    </section>
  );
}

/* ── the work status card ──────────────────────────────────────────────── */

export type WorkState = 'out' | 'in' | 'break' | 'done';

/**
 * Which of the four states the person is in. The status route is the authority for in and on
 * break (`GET /shifts/isCurrentlyPunchedIn`); it knows nothing of a day already finished, so
 * "done" is read from today's own shifts: one with both punches and no open one.
 */
export function workState(st: PunchStatus | undefined, todays: ApiShift[]): WorkState {
  if (st?.isPunchedIn) return st.isOnLunch ? 'break' : 'in';
  return todays.some(isPunched) ? 'done' : 'out';
}

/**
 * The first card of Min side and the head of Vaktdetaljer: one state, said once, and only the
 * actions that state allows. A break is offered only where the company records breaks and none
 * has been taken on this shift; on a break the only way on is to end it (brief rules 1 and 8).
 */
export function WorkCard({
  st,
  loading,
  todays,
  nextShift,
  onDetails,
  compact,
  shifts = 'success',
}: {
  st: PunchStatus | undefined;
  loading: boolean;
  todays: ApiShift[];
  nextShift?: ApiShift | null;
  onDetails?: (s: ApiShift) => void;
  compact?: boolean;
  /** where today's shifts stand: until they answer, never «ingen planlagt vakt» (#272 B-30) */
  shifts?: 'pending' | 'error' | 'success';
}) {
  const { tenant } = useTenant();
  const punch = usePunch();
  const now = useNow();
  const day0 = today();
  const state = workState(st, todays);
  // hours per project (2026-09-29): the project to clock in on, the last one chosen by default
  const projectsOn = useProjectsOn();
  const projectWord = useProjectWord();
  const projects = (useProjects().data ?? []).filter((p) => p.active);
  const [projectId, setProjectId] = useState<number | ''>(() => {
    try {
      return Number(localStorage.getItem('tf_last_project')) || '';
    } catch {
      return '';
    }
  });
  const chosen = projects.some((p) => p.id === projectId) ? projectId : '';
  const punchIn = () => {
    if (chosen)
      try {
        localStorage.setItem('tf_last_project', String(chosen));
      } catch {
        /* private mode: the choice is not remembered */
      }
    return punch.punchIn.mutateAsync(projectsOn && chosen ? { project_id: chosen } : undefined);
  };
  const run = async (ok: string, f: () => Promise<unknown>) => {
    try {
      await f();
      toast(T(ok));
    } catch (e) {
      toast(errorText(e));
    }
  };
  const busy =
    punch.punchIn.isPending ||
    punch.punchOut.isPending ||
    punch.lunchIn.isPending ||
    punch.lunchOut.isPending;
  // the open shift, with its type and tasks from the list when the list has it
  const current = st?.shift ? (todays.find((s) => s.id === st.shift?.id) ?? st.shift) : null;
  const todayShift = todays.find((s) => !isPunched(s) && !s.punch_in_time) ?? null;
  const upcoming = todayShift ?? nextShift ?? null;

  const nowHm = oslo(now.toISOString())?.time ?? '';
  // the type's own name, or nothing: a source without the type (the shifts list failed) said «Vakt» for «Åpning» (#272 B-R09)
  const named = (s: ApiShift) => (s.shiftType?.name ? `, ${s.shiftType.name}` : '');
  const upcomingLine = (s: ApiShift) => {
    const d = dayOf(s);
    const at = oslo(s.start_time)?.time ?? '';
    if (d === day0)
      return at <= nowHm
        ? `${T('Vakt i dag')} ${span(s)}${named(s)}, ${T('ikke stemplet')}`
        : `${T('Neste vakt i dag')} ${span(s)}${named(s)}`;
    return `${T('Neste vakt')} ${longDay(d)} ${span(s)}${named(s)}`;
  };

  let icon: IconName = 'clock';
  let tone = 'off';
  let title = '';
  let big: string | null = null;
  const lines: ReactNode[] = [];
  const actions: ReactNode[] = [];

  if (state === 'in' && st) {
    tone = 'on';
    title = T('På jobb');
    big = dur(minutesBetween(st.lastPunchedInTime, now));
    lines.push(
      <span key="in">
        {T('Stemplet inn')} {oslo(st.lastPunchedInTime)?.time}
        {current ? `, ${shiftName(current)} ${span(current)}` : ''}
      </span>,
    );
    if (st.lunchInTime && st.lunchOutTime)
      lines.push(
        <span key="br">
          {T('Pause')} {oslo(st.lunchInTime)?.time}–{oslo(st.lunchOutTime)?.time}
        </span>,
      );
    else if (modeOf(tenant) === 'auto') lines.push(<span key="auto">{T('Pauser trekkes automatisk.')}</span>);
    else if (modeOf(tenant) === 'punched_min')
      lines.push(
        <span key="min">
          {T('Stempler du ingen pause, trekkes {min} min.', { min: tenant?.lunch_length_minutes ?? 30 })}
        </span>,
      );
    if (recordsBreaks(tenant) && !st.lunchInTime)
      actions.push(
        <Button
          key="pause"
          tone="pri"
          disabled={busy}
          onClick={() => run('Pause startet', () => punch.lunchIn.mutateAsync())}
        >
          {T('Ta pause')}
        </Button>,
      );
    actions.push(
      <Button
        key="out"
        {...(actions.length ? {} : { tone: 'pri' as const })}
        disabled={busy}
        onClick={() => run('Stemplet ut. Timene går til godkjenning.', () => punch.punchOut.mutateAsync())}
      >
        {T('Stemple ut')}
      </Button>,
    );
  } else if (state === 'break' && st) {
    tone = 'brk';
    icon = 'square';
    title = T('På pause');
    big = dur(minutesBetween(st.lunchInTime, now));
    lines.push(
      <span key="since">
        {T('Pause siden')} {oslo(st.lunchInTime)?.time}
      </span>,
      <span key="before">
        {T('Jobbet før pausen')}: {dur(minutesBetween(st.lastPunchedInTime, st.lunchInTime))}
      </span>,
    );
    // ended here even if the company has since stopped recording breaks (TF-BREAK-RECOVERY)
    actions.push(
      <Button
        key="end"
        tone="pri"
        disabled={busy}
        onClick={() => run('Pausen er avsluttet', () => punch.lunchOut.mutateAsync())}
      >
        {T('Avslutt pause')}
      </Button>,
    );
  } else if (state === 'done') {
    tone = 'done';
    icon = 'check';
    title = T('Ferdig for i dag');
    const w = workedSum(todays.filter(isPunched), breakPolicy(tenant));
    big = w == null ? null : dur(w * 60);
    const last = todays.filter(isPunched).at(-1);
    if (last)
      lines.push(
        <span key="punched">
          {T('Stemplet')} {oslo(last.punch_in_time)?.time}–{oslo(last.punch_out_time)?.time}
          {w == null ? '' : `, ${T('etter pause')}`}
        </span>,
      );
    if (nextShift && dayOf(nextShift) > day0) lines.push(<span key="next">{upcomingLine(nextShift)}</span>);
    // a second stint the same day is allowed; it is not the day's main action any more
    actions.push(
      <Button key="again" small disabled={busy || loading} onClick={() => run('Stemplet inn', punchIn)}>
        {T('Stemple inn igjen')}
      </Button>,
    );
  } else {
    title = T('Ikke stemplet inn');
    lines.push(
      <span key="up">
        {upcoming
          ? upcomingLine(upcoming)
          : shifts === 'error'
            ? T('Kunne ikke hente vakten din')
            : shifts === 'pending'
              ? T('Henter…')
              : T('Ingen planlagt vakt i dag')}
      </span>,
    );
    actions.push(
      <Button key="in" tone="pri" disabled={busy || loading} onClick={() => run('Stemplet inn', punchIn)}>
        <Icon name="clock" />
        {T('Stemple inn')}
      </Button>,
    );
  }
  const detail = state === 'in' || state === 'break' ? current : todayShift;
  if (detail && onDetails && !compact)
    actions.push(
      <Button key="det" onClick={() => onDetails(detail)}>
        <Icon name="calendar" />
        {T('Se vaktdetaljer')}
      </Button>,
    );

  return (
    <div className={`ework ${tone}`} data-testid={compact ? 'punch-detail' : 'punch'} data-state={state}>
      <div className="ewhead">
        <span className="ewic" aria-hidden="true">
          <Icon name={icon} size={22} />
        </span>
        <div>
          <b className="ewt">{title}</b>
          {big && (
            <span className="ewbig" data-testid="elapsed">
              {big}
            </span>
          )}
          {lines.map((l, i) => (
            <span className="ewl" key={(l as { key?: string }).key ?? i}>
              {l}
            </span>
          ))}
        </div>
      </div>
      {projectsOn && !compact && state === 'out' && projects.length > 0 && (
        <label className="fld ewproj" data-testid="punch-project">
          <span>{T('Hva jobber du med? (valgfritt)')}</span>
          <select className="inp" value={chosen} onChange={(e) => setProjectId(Number(e.target.value) || '')}>
            <option value="">{projectWord.none}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {projectLabel(p)}
              </option>
            ))}
          </select>
        </label>
      )}
      {projectsOn && (state === 'in' || state === 'break') && current && (
        <p className="ewl" data-testid="punch-project-now">
          {projectWord.one}:{' '}
          <b>
            {projectLabel(
              projects.find((p) => p.id === (current as { project_id?: number | null }).project_id),
            )}
          </b>
        </p>
      )}
      {/* «Bytt prosjekt» (#115): the shift splits at the switch, clocked out and in at the same second */}
      {projectsOn && !compact && state === 'in' && current && projects.length > 0 && (
        <label className="fld ewproj" data-testid="punch-switch">
          <span>{T('Bytt {what}', { what: projectWord.lower })}</span>
          <select
            className="inp"
            value=""
            disabled={punch.switchProject.isPending}
            onChange={(e) => {
              const id = Number(e.target.value);
              if (id)
                void run(T('Byttet {what}', { what: projectWord.lower }), () =>
                  punch.switchProject.mutateAsync({ project_id: id }),
                );
            }}
          >
            <option value="">{T('Velg …')}</option>
            {projects
              .filter((p) => p.id !== (current as { project_id?: number | null }).project_id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {projectLabel(p)}
                </option>
              ))}
          </select>
        </label>
      )}
      <div className="ewact">{actions}</div>
      {state === 'out' && !todayShift && !compact && shifts === 'success' && (
        <p className="hint" data-testid="adhoc">
          <Icon name="info" />{' '}
          {T(
            'Du har ingen planlagt vakt i dag. Stempler du inn, registreres tiden uten planlagt vakt, fra innstempling til utstempling.',
          )}
        </p>
      )}
    </div>
  );
}

/* ── open shifts ───────────────────────────────────────────────────────── */

/**
 * Asking for an open shift. `POST /shifts/:id/claim` writes a request the manager decides on; it
 * assigns nothing (user/shiftOfferController.js claimOpenShift). No route lists an employee's own
 * claims, so "asked" is what this page has been told: the 201, or the 400 a repeat is answered with.
 */
export function useAsk() {
  const claim = useClaim();
  const [asked, setAsked] = useState<Set<number>>(new Set());
  const ask = async (id: number) => {
    try {
      await claim.mutateAsync({ id });
      setAsked((x) => new Set(x).add(id));
      toast(T('Du har bedt om vakten. Lederen ser det under Varsler.'));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'already_asked') setAsked((x) => new Set(x).add(id));
      toast(errorText(e));
    }
  };
  return { asked, ask, pending: claim.isPending };
}
export type Ask = ReturnType<typeof useAsk>;

/** The deliberate action on an open shift: a button, never the row. */
export function TakeButton({ s, a }: { s: ApiShift; a: Ask }) {
  return a.asked.has(s.id) ? (
    <Chip tone="warn" small>
      {T('Forespurt, lederen avgjør')}
    </Chip>
  ) : (
    <Button small onClick={() => a.ask(s.id)} disabled={a.pending}>
      {T('Ta vakt')}
    </Button>
  );
}

export function OpenRow({
  s,
  a,
  onOpen,
  showDate = true,
}: { s: ApiShift; a: Ask; onOpen: () => void; showDate?: boolean }) {
  return (
    <div className="erow open" data-testid="open-shift">
      {showDate && <DateBox d={dayOf(s)} />}
      <button type="button" className="lnkrow" onClick={onOpen}>
        <span className="ebody">
          <b>
            {T('Ledig vakt')} <span className="mono">{span(s)}</span>{' '}
            <small className="nw">({num(lengthH(s), 1)} t)</small>
          </b>
          <span>{shiftName(s)}</span>
        </span>
      </button>
      <TakeButton s={s} a={a} />
    </div>
  );
}

export function OpenDrawer({ s, a, onClose }: { s: ApiShift | null; a: Ask; onClose: () => void }) {
  const { tenant } = useTenant();
  return (
    <Drawer open={!!s} onClose={onClose} title={T('Ledig vakt')} sub={s ? longDay(dayOf(s)) : ''}>
      {s && (
        <div data-testid="open-detail">
          <div className="prow">
            <b>{T('Tid')}</b>
            <span className="pv mono">
              {span(s)} ({num(lengthH(s), 1)} t)
            </span>
          </div>
          <div className="prow">
            <b>{T('Vakt')}</b>
            <span className="pv">{shiftName(s)}</span>
          </div>
          <div className="prow">
            <b>{T('Arbeidssted')}</b>
            <span className="pv">{tenant?.name ?? ''}</span>
          </div>
          {s.description ? (
            <div className="prow">
              <b>{T('Notater')}</b>
              <span className="pv">{s.description}</span>
            </div>
          ) : null}
          <p className="hint">
            {T('Lederen velger hvem som får vakten. Får du den, flyttes den til planen din.')}
          </p>
          <div className="eact">
            <TakeButton s={s} a={a} />
          </div>
        </div>
      )}
    </Drawer>
  );
}

/* ── swaps ─────────────────────────────────────────────────────────────── */

const whenOf = (start?: string, end?: string) => {
  const d = oslo(start ?? '')?.date ?? '';
  return d ? `${DNL[dow(d)]} ${fmtD(d)} ${oslo(start ?? '')?.time}–${oslo(end ?? '')?.time}` : '';
};

/** The swap state in words. */
export function swapChip(x: ApiSwap) {
  return x.status === 'approved_by_manager' ? (
    <Chip tone="ok" small>
      {T('Godkjent')}
    </Chip>
  ) : x.status === 'pending_manager_approval' ? (
    <Chip tone="warn" small>
      {T('Til godkjenning')}
    </Chip>
  ) : x.status === 'pending_user_approval' ? (
    <Chip tone="warn" small>
      {T('Venter')}
    </Chip>
  ) : (
    <Chip tone="dgr" small>
      {T('Avslått')}
    </Chip>
  );
}

/** The swaps this person is part of, with Godta and Avslå on one that waits for them. */
export function SwapList({ swaps }: { swaps: ApiSwap[] }) {
  const user = useAuth((s) => s.user);
  const sw = useSwapWrites();
  const run = async (ok: string, f: () => Promise<unknown>) => {
    try {
      await f();
      toast(T(ok));
    } catch (e) {
      toast(errorText(e));
    }
  };
  return (
    <>
      {swaps.map((x) => {
        const mineIsOne = x.offered_by_one === user?.id;
        const ask = x.offered_by_two === user?.id && x.status === 'pending_user_approval';
        const mine = mineIsOne ? x.shiftOne : x.shiftTwo;
        const theirs = mineIsOne ? x.shiftTwo : x.shiftOne;
        return (
          <div className="erow static" key={x.id} data-testid="my-swap">
            <span className="ebody">
              <b>
                {T('Din vakt')}: {whenOf(mine?.start_time, mine?.end_time)}
              </b>
              <span>
                {T('Vakten du ønsker')}: {whenOf(theirs?.start_time, theirs?.end_time)},{' '}
                {fullName(mineIsOne ? x.offeredByUserTwo : x.offeredByUserOne)}
                {x.employee_comment ? `, ${x.employee_comment}` : ''}
              </span>
            </span>
            {swapChip(x)}
            {ask && (
              <span className="econtact">
                <Button
                  small
                  tone="pri"
                  onClick={() =>
                    run('Byttet er godtatt. Lederen godkjenner.', () => sw.accept.mutateAsync(x.id))
                  }
                >
                  {T('Godta')}
                </Button>
                <Button small onClick={() => run('Byttet er avslått.', () => sw.decline.mutateAsync(x.id))}>
                  {T('Avslå')}
                </Button>
              </span>
            )}
          </div>
        );
      })}
    </>
  );
}

/**
 * Proposing a swap for one of my shifts. A 4xx wrote nothing and keeps the form; anything else
 * leaves the outcome unknown, so the swaps are read back and decide, and nothing is sent twice.
 */
function SwapDrawer({ shift, onClose }: { shift: ApiShift | null; onClose: () => void }) {
  const user = useAuth((s) => s.user);
  const day0 = today();
  const sw = useSwapWrites();
  const candQ = useSwapCandidates(day0, addDays(day0, 13), !!shift);
  const swapsQ = useMySwaps();
  const [pick, setPick] = useState({ who: 0, shift: 0, note: '' });
  const [phase, setPhase] = useState<'idle' | 'sending' | 'blocked'>('idle');
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const cands = candQ.data ?? [];
  const people = [...new Map(cands.flatMap((c) => (c.user ? [[c.user.id, c.user] as const] : []))).values()];
  const whoId = pick.who || people[0]?.id || 0;
  const theirs = cands.filter((c) => c.user?.id === whoId);
  const picked = theirs.find((c) => c.id === pick.shift) ?? theirs[0];
  const close = () => {
    setPick({ who: 0, shift: 0, note: '' });
    setErr('');
    setInfo('');
    setPhase('idle');
    onClose();
  };
  const recheck = async () => {
    const r = await swapsQ.refetch();
    if (r.status === 'error') return; // still blocked; the read retry stays on screen
    const list = r.data ?? [];
    const settled = T('Status hentet på nytt. Viser det som er lagret.');
    if (list.some((x) => x.shift_id_one === shift?.id)) {
      close();
      toast(settled);
      return;
    }
    setErr('');
    setInfo(settled);
    setPhase('idle');
  };
  const send = async () => {
    if (!shift || !picked || phase !== 'idle') return;
    setPhase('sending');
    setErr('');
    setInfo('');
    try {
      await sw.propose.mutateAsync({
        shift_id_one: shift.id,
        shift_id_two: picked.id,
        employee_comment: pick.note || null,
      });
      close();
      toast(T('Forslaget er sendt.'));
    } catch (e) {
      const code = e instanceof ApiError ? e.status : undefined;
      if (code != null && code >= 400 && code < 500 && code !== 499) {
        setErr((e as Error).message);
        setPhase('idle');
        return;
      }
      setErr(T('Vi vet ikke om forslaget ble sendt. Sjekk status før du prøver igjen.'));
      setPhase('blocked');
      void recheck();
    }
  };
  return (
    <Drawer
      open={!!shift}
      onClose={close}
      title={T('Foreslå vaktbytte')}
      sub={shift ? `${whenOf(shift.start_time, shift.end_time)}, ${shiftName(shift)}` : ''}
    >
      {candQ.isSuccess && people.length === 0 ? (
        <Empty>{T('Ingen vakter å bytte med de neste to ukene.')}</Empty>
      ) : (
        <>
          <div className="fld">
            <span className="lbl">{T('Kollega')}</span>
            <select
              className="inp w"
              aria-label={T('Kollega')}
              value={whoId}
              onChange={(e) => setPick({ ...pick, who: Number(e.target.value), shift: 0 })}
            >
              {people.map((pp) => (
                <option key={pp.id} value={pp.id}>
                  {fullName(pp)}
                </option>
              ))}
            </select>
          </div>
          <div className="fld">
            <span className="lbl">{T('Vakten du ønsker')}</span>
            <select
              className="inp w"
              aria-label={T('Vakten du ønsker')}
              value={picked?.id ?? ''}
              onChange={(e) => setPick({ ...pick, shift: Number(e.target.value) })}
            >
              {theirs.map((c: ApiSwapCandidate) => (
                <option key={c.id} value={c.id}>
                  {`${whenOf(c.start_time, c.end_time)}, ${c.shiftType?.name ?? ''}`}
                </option>
              ))}
            </select>
          </div>
          <div className="fld">
            <span className="lbl">{T('Merknad (valgfritt)')}</span>
            <input
              className="inp w"
              aria-label={T('Merknad til byttet')}
              value={pick.note}
              onChange={(e) => setPick({ ...pick, note: e.target.value })}
            />
          </div>
          <div data-testid="swap-summary">
            <div className="prow">
              <b>{T('Din vakt')}</b>
              <span className="pv">
                {whenOf(shift?.start_time, shift?.end_time)}, {user ? fullName(user) : ''}
              </span>
            </div>
            <div className="prow">
              <b>{T('Vakten du ønsker')}</b>
              <span className="pv">
                {whenOf(picked?.start_time, picked?.end_time)}, {picked?.shiftType?.name ?? ''},{' '}
                {fullName(picked?.user)}
              </span>
            </div>
          </div>
          {info ? <span data-testid="swap-info">{info}</span> : null}
          {err ? (
            <span role="alert" style={{ color: 'var(--dgr-i)' }}>
              {err}
            </span>
          ) : null}
          <div className="eact">
            {phase === 'blocked' ? <Button onClick={recheck}>{T('Prøv igjen')}</Button> : null}
            <Button tone="pri" disabled={!picked || phase !== 'idle'} onClick={send}>
              <Icon name="upload" />
              {T('Send forslag')}
            </Button>
          </div>
        </>
      )}
    </Drawer>
  );
}

/* ── Vaktdetaljer ──────────────────────────────────────────────────────── */

/**
 * One shift, in a drawer: what, when, where, the attendance state when it is the open shift,
 * the break rule, the employer's note, the tasks with their progress, and who works at the same
 * time. The person's own actions on it (offer it away, propose a swap) sit at the foot. There is
 * no "report a problem": the product has no such workflow.
 */
export function ShiftDrawer({
  shift,
  onClose,
  st,
  todays,
}: {
  shift: ApiShift | null;
  onClose: () => void;
  st?: PunchStatus | undefined;
  todays?: ApiShift[];
}) {
  const { tenant, workspace } = useTenant();
  const user = useAuth((s) => s.user);
  const w = useMyWrites();
  const [swapFor, setSwapFor] = useState<ApiShift | null>(null);
  const d = shift ? dayOf(shift) : today();
  const seeColleagues = !!tenant && workspace.enabled.scheduling && workspace.visibility.coworker_shifts;
  const dayQ = useShifts(d, d, !!shift && seeColleagues);
  const colleagues = seeColleagues
    ? (dayQ.data?.shifts ?? []).filter(
        (x) =>
          x.published &&
          x.user_id != null &&
          x.user_id !== user?.id &&
          shift != null &&
          x.start_time < shift.end_time &&
          x.end_time > shift.start_time,
      )
    : [];
  const open = st?.shift && shift && st.shift.id === shift.id;
  const started = shift ? new Date(shift.start_time) <= new Date() : false;
  const t = taskCount(shift);
  const run = async (ok: string, f: () => Promise<unknown>) => {
    try {
      await f();
      toast(T(ok));
    } catch (e) {
      toast(errorText(e));
    }
  };
  return (
    <>
      <Drawer
        open={!!shift}
        onClose={onClose}
        title={shift ? shiftName(shift) : T('Vaktdetaljer')}
        sub={shift ? `${T('Vaktdetaljer')}, ${longDay(d)}, ${span(shift)}` : ''}
      >
        {shift && (
          <div data-testid="shift-detail">
            {open && st ? (
              <WorkCard st={st} loading={false} todays={todays ?? []} compact />
            ) : (
              <p style={{ margin: '0 0 8px' }}>
                <ShiftChip s={shift} />
              </p>
            )}
            <div className="prow">
              <b>{T('Tid')}</b>
              <span className="pv mono">
                {span(shift)} ({num(lengthH(shift), 1)} t)
              </span>
            </div>
            <div className="prow">
              <b>{T('Arbeidssted')}</b>
              <span className="pv">{tenant?.name ?? ''}</span>
            </div>
            <div className="prow">
              <b>{T('Pause')}</b>
              <span className="pv">{breakForEmployee(tenant)}</span>
            </div>
            {shift.description ? (
              <div className="prow">
                <b>{T('Notater')}</b>
                <span className="pv">{shift.description}</span>
              </div>
            ) : null}
            {t.total > 0 && (
              <section className="edsec" data-testid="shift-tasks">
                <h3>
                  <Icon name="list" />
                  {T('Oppgaver')} <span className="sub">{T('{done} av {total} fullført', t)}</span>
                </h3>
                <Progress {...t} />
                <Tasks tasks={shift.taskAssignments} />
              </section>
            )}
            {seeColleagues && (
              <section className="edsec" data-testid="shift-colleagues">
                <h3>{T('Jobber samtidig')}</h3>
                {colleagues.length ? (
                  colleagues.map((c) => (
                    <div className="eperson" key={c.id}>
                      <Avatar u={c.user} />
                      <span className="ebody">
                        <b>{fullName(c.user)}</b>
                        <span>
                          {shiftName(c)}, <span className="mono">{span(c)}</span>
                        </span>
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="hint">{T('Ingen andre på vakt samtidig.')}</p>
                )}
              </section>
            )}
            {shift.published && !shift.punch_in_time && shift.user_id === user?.id && (
              <section className="edsec">
                <h3>{T('Kan du ikke ta vakten?')}</h3>
                {started ? (
                  <p className="hint">{T('Vakten har startet og kan ikke tilbys bort.')}</p>
                ) : (
                  <div className="eact">
                    {shift.is_offer_requested ? (
                      <Button
                        onClick={() => run('Tilbudet er trukket', () => w.revokeOffer.mutateAsync(shift.id))}
                      >
                        {T('Trekk tilbud')}
                      </Button>
                    ) : (
                      <Button
                        onClick={() =>
                          // read again at the click; the server stays the authority
                          new Date(shift.start_time) <= new Date()
                            ? toast(T('Vakten har startet og kan ikke tilbys bort.'))
                            : run('Vakten er tilbudt bort. Lederen godkjenner når noen tar den.', () =>
                                w.offer.mutateAsync({ shift_id: shift.id }),
                              )
                        }
                      >
                        {T('Tilby bort')}
                      </Button>
                    )}
                    {!shift.is_swap_requested && (
                      <Button onClick={() => setSwapFor(shift)}>{T('Foreslå vaktbytte')}</Button>
                    )}
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </Drawer>
      <SwapDrawer shift={swapFor} onClose={() => setSwapFor(null)} />
    </>
  );
}
